import React, { useState } from "react";
import { Button, Tooltip } from "@mui/material";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import NoRecord from "../../../assets/SharedComponents/Table/NoRecord";
import { FaFilterIcon } from "../../../assets/Images/icon";
import { ACCESS_STATUSES, formatDate, pillClass, toastError, toastSuccess } from "../helpers";
import usePagedList from "./usePagedList";
import { LoadingRow, NoteModal, PerPageSelect, SimplePager } from "./Shared";

const Prerequisites = ({ value }) => {
  if (!value) return "-";
  const item = (ok, label) => (
    <div key={label}>
      <span className={ok ? "pill pill-green" : "pill pill-red"}>{ok ? "✓" : "✗"}</span> {label}
    </div>
  );

  // Shown for context, not as a pass/fail: an import feed no longer disqualifies a dealership.
  // The duplicate protection is per listing now — a feed-mapped dealer may adopt and update its
  // feed vehicles, and only a push matching nothing already on the site is refused, with
  // `feed_listing_unmatched`. Rendering it as a red ✗ next to the two real prerequisites made
  // eligible applicants look ineligible and invited a wrong rejection.
  const note = (text) => (
    <div key={text} style={{ opacity: 0.75 }}>
      <span className="pill">i</span> {text}
    </div>
  );

  return (
    <>
      {item(value.address_principal, "Principal address")}
      {item(value.subscription_active, "Active subscription")}
      {value.active_import_feed
        ? note("Also fed by an import feed — not a blocker")
        : note("No import feed")}
    </>
  );
};

/**
 * Access requests queue: approve / reject pending requests, revoke an approved access (every key
 * goes with it), and the per-dealership shortcuts (quotas, issue a master key, activity).
 */
const RequestsTab = ({ access, superAdmin, onOpenQuotas, onIssueKey, onShowActivity }) => {
  const [status, _status] = useState("pending");
  const [keywordInput, _keywordInput] = useState("");
  const [keyword, _keyword] = useState("");
  const [perPage, _perPage] = useState("");
  const [dialog, _dialog] = useState(null); // { kind: approve|reject|revoke, row }
  const [busy, _busy] = useState(false);

  const list = usePagedList("/access-requests", {
    "filter[status]": status,
    keyword,
    per_page: perPage,
  });

  const clearFilter = () => {
    _status("pending");
    _keywordInput("");
    _keyword("");
    _perPage("");
  };

  const submit = (note) => {
    const { kind, row } = dialog;
    const request =
      kind === "revoke"
        ? API.post(`${APP_URLS.DEALER_API}/access/${row.subsidiary_id}/revoke`, { note })
        : API.patch(`${APP_URLS.DEALER_API}/access-requests/${row.id}`, { action: kind, decision_note: note });

    _busy(true);
    request
      .then((res) => {
        if (res.data.success === true) {
          toastSuccess(res.data.message);
          _dialog(null);
          list.reload();
        } else {
          toastError({ message: res.data.message });
        }
      })
      .catch(toastError)
      .finally(() => _busy(false));
  };

  const dialogProps = {
    approve: {
      title: "Approve Dealer API access",
      message:
        "The prerequisites are checked again, the dealership's inventory is indexed and the owner is e-mailed. The owner can then generate keys.",
      confirmLabel: "Approve",
    },
    reject: {
      title: "Reject the access request",
      message: "The owner is e-mailed and may apply again.",
      confirmLabel: "Reject",
      danger: true,
    },
    revoke: {
      title: "Revoke Dealer API access",
      message:
        "Every key of this dealership stops working immediately and the owner is e-mailed. Listings already published stay online.",
      confirmLabel: "Revoke access",
      danger: true,
    },
  };

  const actionsFor = (row) => {
    const buttons = [];

    if (row.status === "pending" && access.canUpdate) {
      buttons.push(
        <Button key="approve" variant="contained" className="bg-success" onClick={() => _dialog({ kind: "approve", row })}>
          Approve
        </Button>,
        <Button key="reject" variant="contained" className="bg-danger" onClick={() => _dialog({ kind: "reject", row })}>
          Reject
        </Button>
      );
    }

    if (row.status === "approved") {
      // A soft-deleted dealership comes back as `subsidiary: null`. Both modals act on that object
      // — the quota one keys its `open` off it and would simply never appear — and the endpoints
      // behind them bind a live subsidiary anyway, so the actions cannot succeed. Revoke is the
      // exception: it posts to `subsidiary_id` directly, so it stays, and closing the access out is
      // exactly what is left to do here.
      if (access.canAdd && superAdmin && row.subsidiary) {
        buttons.push(
          <Button key="issue" variant="contained" onClick={() => onIssueKey(row.subsidiary)}>
            Issue master key
          </Button>
        );
      }
      if (access.canUpdate) {
        if (row.subsidiary) {
          buttons.push(
            <Button key="quotas" variant="outlined" onClick={() => onOpenQuotas(row.subsidiary)}>
              Quotas
            </Button>
          );
        }
        buttons.push(
          <Button key="revoke" variant="contained" className="bg-danger" onClick={() => _dialog({ kind: "revoke", row })}>
            Revoke access
          </Button>
        );
      }
    }

    return buttons.length ? <div className="action-btns">{buttons}</div> : "-";
  };

  return (
    <>
      <div className="filters">
        <select className="filter-select" value={status} onChange={(e) => _status(e.target.value)}>
          <option value="">All statuses</option>
          {ACCESS_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.name}
            </option>
          ))}
        </select>
        <input
          className="filter-input"
          placeholder="Dealership name or slug"
          maxLength={100}
          value={keywordInput}
          onChange={(e) => _keywordInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && _keyword(keywordInput.trim())}
        />
        <button className="btn btn-secondary" onClick={() => _keyword(keywordInput.trim())}>
          Search
        </button>
        <PerPageSelect value={perPage} onChange={_perPage} />
        <Tooltip title="Clear Filter" placement="bottom">
          <FaFilterIcon className="filter-icon" role="button" onClick={clearFilter} />
        </Tooltip>
      </div>

      <div className="tableContainer">
        <table className="auctionTable">
          <thead className="tableHead">
            <tr className="table-row">
              <th className="time-start">Dealership</th>
              <th className="time-start">Requested by</th>
              <th className="time-start">Request note</th>
              <th className="time-start">Prerequisites</th>
              <th className="time-start">Status</th>
              <th className="time-start">Actions</th>
            </tr>
          </thead>
          {list.loading ? (
            <LoadingRow colSpan={6} />
          ) : (
            <tbody>
              {list.rows.length > 0 ? (
                list.rows.map((row) => (
                  <tr key={`request-${row.id}`} className="table-row">
                    <td>
                      <b>{row.subsidiary?.name ?? `#${row.subsidiary_id}`}</b>
                      <div className="muted">
                        {row.subsidiary?.slug} · #{row.subsidiary_id}
                      </div>
                      <button className="link muted" onClick={() => onShowActivity(row.subsidiary_id)}>
                        Push activity
                      </button>
                    </td>
                    <td>
                      {row.requested_by?.name ?? `User #${row.requested_by_user_id}`}
                      <div className="muted">{row.requested_by?.email}</div>
                      <div className="muted">{formatDate(row.requested_at)}</div>
                    </td>
                    <td className="text-start">{row.request_note || "-"}</td>
                    <td className="text-start">
                      <Prerequisites value={row.subsidiary?.prerequisites} />
                    </td>
                    <td>
                      <span className={pillClass(row.status)}>{row.status}</span>
                      {row.decided_at && (
                        <div className="muted">
                          {formatDate(row.decided_at)}
                          {row.decided_by ? ` by ${row.decided_by.name}` : ""}
                        </div>
                      )}
                      {row.decision_note && <div className="muted">“{row.decision_note}”</div>}
                    </td>
                    <td>{actionsFor(row)}</td>
                  </tr>
                ))
              ) : (
                <NoRecord />
              )}
            </tbody>
          )}
        </table>
      </div>
      <SimplePager page={list.page} hasPrev={list.hasPrev} hasNext={list.hasNext} onChange={list.setPage} />

      <NoteModal
        open={dialog !== null}
        {...(dialog ? dialogProps[dialog.kind] : {})}
        audience="the dealer sees it"
        busy={busy}
        onConfirm={submit}
        onClose={() => _dialog(null)}
      />
    </>
  );
};

export default RequestsTab;
